'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface Deal {
  id: string;
  dealId: string;
  brand: string;
  agency?: string;
  date: string;
  deliverables: string;
  status: 'completed' | 'in_progress' | 'pending';
  category: string;
  isPrivate: boolean;
  campaignValue?: string;
}

const deals: Deal[] = [
  { id: '1', dealId: 'DZ-2026-0001842', brand: 'Djezzy', date: '12 Aoû 2026', deliverables: '1 Reel + 3 Stories', status: 'completed', category: 'Télécoms', isPrivate: true, campaignValue: '350 000 DZD' },
  { id: '2', dealId: 'DZ-2026-0001756', brand: 'Ooredoo Algérie', date: '28 Juil 2026', deliverables: '2 Reels + 5 Stories', status: 'completed', category: 'Télécoms', isPrivate: true, campaignValue: '420 000 DZD' },
  { id: '3', dealId: 'DZ-2026-0001690', brand: 'Cevital', agency: 'Agence Créa+', date: '10 Juil 2026', deliverables: '1 Reel + 2 Posts', status: 'completed', category: 'Agroalimentaire', isPrivate: true, campaignValue: '280 000 DZD' },
  { id: '4', dealId: 'DZ-2026-0001621', brand: 'Mobilis', date: '22 Juin 2026', deliverables: '3 Stories + 1 Post', status: 'completed', category: 'Télécoms', isPrivate: true },
  { id: '5', dealId: 'DZ-2026-0001580', brand: 'Condor Electronics', agency: 'MediaDZ', date: '05 Juin 2026', deliverables: '1 Reel + 4 Stories', status: 'completed', category: 'Électronique', isPrivate: true },
  { id: '6', dealId: 'DZ-2026-0001901', brand: 'Yassir', date: '18 Aoû 2026', deliverables: '2 Reels', status: 'in_progress', category: 'Tech/App', isPrivate: true },
];

const statusMap = {
  completed: { label: 'Complété', cls: 'bg-positive/10 text-positive border-positive/20' },
  in_progress: { label: 'En cours', cls: 'bg-info/10 text-info border-info/20' },
  pending: { label: 'En attente', cls: 'bg-warning/10 text-warning border-warning/20' },
};

export default function VerifiedDealsPage() {
  const [showPrivate, setShowPrivate] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [filter, setFilter] = useState('all');

  const categories = ['all', ...Array.from(new Set(deals.map(d => d.category)))];
  const filtered = filter === 'all' ? deals : deals.filter(d => d.category === filter);

  return (
    <AppLayout currentPath="/verified-deals">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-700 uppercase tracking-widest text-positive bg-positive/10 px-2 py-0.5 rounded-full">Verified Deal Registry</span>
            </div>
            <h1 className="text-2xl font-700 text-foreground">Registre des Collaborations Vérifiées</h1>
            <p className="text-sm text-muted-foreground mt-1">Historique officiel ISX · Identifiants uniques par collaboration</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowPrivate(!showPrivate)} className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm font-600 transition-colors ${showPrivate ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground'}`}>
              <Icon name={showPrivate ? 'EyeIcon' : 'EyeSlashIcon'} size={14} />
              {showPrivate ? 'Masquer valeurs' : 'Voir valeurs (Admin)'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Collaborations vérifiées', value: '42', icon: 'CheckBadgeIcon', color: 'text-positive' },
            { label: 'En cours', value: '1', icon: 'ClockIcon', color: 'text-info' },
            { label: 'Marques partenaires', value: '18', icon: 'BuildingOffice2Icon', color: 'text-primary' },
            { label: 'Catégories', value: '7', icon: 'TagIcon', color: 'text-accent' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-600">{s.label}</span>
                <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={16} className={s.color} />
              </div>
              <p className="text-metric-md font-mono text-foreground">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Legal disclaimer */}
        <div className="bg-muted/40 border border-border rounded-xl p-4 flex gap-3">
          <Icon name="InformationCircleIcon" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">L'ISX Deal ID est un identifiant de référence interne ISX. Il ne constitue pas une facture officielle, un numéro fiscal, un titre financier, un token, ni une immatriculation gouvernementale. Les données financières sont privées par défaut et ne sont jamais divulguées publiquement.</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)} className={`px-3 py-1.5 rounded-full text-xs font-600 transition-all ${filter === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
              {cat === 'all' ? 'Toutes' : cat}
            </button>
          ))}
        </div>

        {/* Deals Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">ISX Deal ID</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Marque / Agence</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Livrables</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Catégorie</th>
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Date</th>
                  {showPrivate && <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Valeur (Privé)</th>}
                  <th className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wide text-muted-foreground">Statut</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((deal, i) => (
                  <tr key={deal.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-primary font-600">{deal.dealId}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-600 text-foreground">{deal.brand}</p>
                      {deal.agency && <p className="text-xs text-muted-foreground">via {deal.agency}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{deal.deliverables}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">{deal.category}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{deal.date}</td>
                    {showPrivate && (
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-warning font-600">{deal.campaignValue || '—'}</span>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full border ${statusMap[deal.status].cls}`}>
                        {statusMap[deal.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedDeal(deal)} className="text-muted-foreground hover:text-foreground transition-colors">
                        <Icon name="EyeIcon" size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Deal Detail Modal */}
        {selectedDeal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-700 text-foreground">Détail de la collaboration</h3>
                <button onClick={() => setSelectedDeal(null)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="XMarkIcon" size={18} />
                </button>
              </div>
              <div className="space-y-3">
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-600 mb-1">ISX Deal ID</p>
                  <p className="font-mono text-sm text-primary font-700">{selectedDeal.dealId}</p>
                </div>
                {[
                  { label: 'Marque', value: selectedDeal.brand },
                  { label: 'Agence', value: selectedDeal.agency || 'Directe' },
                  { label: 'Livrables', value: selectedDeal.deliverables },
                  { label: 'Catégorie', value: selectedDeal.category },
                  { label: 'Date', value: selectedDeal.date },
                ].map(item => (
                  <div key={item.label} className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                    <span className="text-xs font-600 text-foreground">{item.value}</span>
                  </div>
                ))}
                <div className="bg-muted/40 rounded-lg p-3 mt-2">
                  <p className="text-[11px] text-muted-foreground">Les données financières (valeur de campagne, factures, contrats) sont privées et ne sont pas affichées publiquement conformément à la politique ISX.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
